import { History } from "history";
import pick from "lodash-es/pick";
import upperFirst from "lodash-es/upperFirst";
import { parse, stringify } from "query-string";
import React from "react";
import { Helmet } from "react-helmet";
import { Route } from "react-router";
import { ListRowProps } from "react-virtualized";

import { ARCHIVED_DAYS_WITH_COVER_PHOTO } from "@/mk2/apps/forum/constants";
import {
    forumBrandDaysArchiveUrl,
    forumCommonPlaceTopicsUrl,
    forumListOfForumsUrl,
    forumTopicsCategoryUrl,
} from "@/mk2/apps/forum/urls";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { InfiniteListLoadable } from "@/mk2/components/InfiniteListLoadable";
import { Link } from "@/mk2/components/Link";
import { ListHeadingItem } from "@/mk2/components/ListHeadingItem";
import { ListItem, ListItemPlaceholder } from "@/mk2/components/ListItem";
import { ListItemIcon } from "@/mk2/components/ListItemIcon";
import { NoResults } from "@/mk2/components/NoResults";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { dateDiff } from "@/mk2/helpers/timeformat";
import { url } from "@/mk2/helpers/urls";
import { LoadingState, isSuccess } from "@/mk2/reducers/loadingState";
import { interpolate } from "@/mk2/services/i18n";
import {
    FORUM_ARCHIVE_CAMPAIGNS_PAGE_TITLE,
    FORUM_COMMON_PLACE,
    FORUM_TOPICS_NOT_FOUND_query,
    LIST_OF_FORUMS_BLUEHORSE,
    LOAD_PREVIOUS,
    NEW_FEMININE,
} from "@/mk/autogenerated/translation";
import assetUrl from "@/mk/common/assetUrl";

import styles from "./ListOfForums.mscss";
import { Props } from "./ListOfForumsPage";

const ROW_HEIGHT = 44;

const lessThan14Days = (d: string | Date) => {
    const { diffDays, diffSeconds } = dateDiff(d, new Date());
    return diffDays < 14;
};

class ListOfForums extends React.PureComponent<Props> {
    public componentDidUpdate(prevProps: Props) {
        // After has changed
        if (this.props.after !== prevProps.after) {
            this.load();
        }
    }

    public render() {
        const { loadingState, items } = this.props;

        return (
            <div className={styles.ListOfForums}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInit}
                    onRenderSuccess={this.renderSuccess}
                    hasData={!!items && items.length > 0}
                />
            </div>
        );
    }

    private renderInit = () => {
        return (
            <>
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast={false} />
                <ListItemPlaceholder icon isLast />
            </>
        );
    };

    private renderSuccess = () => {
        const { hasNext, hasPrevious, items, loadingState, location } = this.props;

        // InfiniteScroll data
        // @see https://github.com/bvaughn/react-virtualized/blob/master/docs/creatingAnInfiniteLoadingList.md
        const topicsRowCount = hasNext ? items.length + 1 : items.length;
        const rowCount = !hasNext && isSuccess(loadingState) ? topicsRowCount + 2 : topicsRowCount;

        return (
            <>
                <Route exact path={forumTopicsCategoryUrl}>
                    {this.renderHeaders()}
                </Route>

                {hasPrevious && (
                    <div className={styles.ListOfForums__loadPrevious}>
                        <Link
                            className={styles.ListOfForums__loadPrevious__link}
                            to={{
                                ...location,
                                search: `?${stringify({ ...parse(location.search), after: -items[0].topicId })}`,
                            }}
                            onClick={this.loadPrevious}
                        >
                            {LOAD_PREVIOUS}
                        </Link>
                    </div>
                )}

                <div className={styles.ListOfForums__spacer} />

                {items.length === 0 ? (
                    <NoResults message={interpolate(FORUM_TOPICS_NOT_FOUND_query, { query: "" })} />
                ) : (
                    <InfiniteListLoadable
                        isRowLoaded={this.isRowLoaded}
                        loadMoreRows={this.loadNext}
                        rowCount={rowCount}
                        rowHeight={ROW_HEIGHT}
                        rowRenderer={(props: ListRowProps) => this.rowRenderer(props)}
                    />
                )}
            </>
        );
    };

    private load = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { onLoad, requestUser, after } = this.props;
        onLoad(requestUser.username, after);
    };

    private loadNext = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { onLoad, requestUser, items } = this.props;
        const after = items[items.length - 1].topicId;
        onLoad(requestUser.username, after);

        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private loadPrevious = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { onLoad, requestUser, items } = this.props;
        const after = -items[0].topicId;
        onLoad(requestUser.username, after);
    };

    private isRowLoaded = ({ index }) => !this.props.hasNext || index < this.props.items.length;

    private rowRenderer = ({ index, key, style }) => {
        const { brandDaysRowNo, hasNext, items, itemToTopicUrl, systemCatRowNo } = this.props;

        if (index === systemCatRowNo) {
            return (
                <ListHeadingItem isSectionStart key={key} style={style}>
                    {LIST_OF_FORUMS_BLUEHORSE}
                </ListHeadingItem>
            );
        }

        if (index === brandDaysRowNo) {
            return (
                <ListItem
                    hasMore
                    icon={<ListItemIcon lazy photo={ARCHIVED_DAYS_WITH_COVER_PHOTO} />}
                    isLast
                    key={key}
                    style={style}
                    to={forumBrandDaysArchiveUrl}
                >
                    {FORUM_ARCHIVE_CAMPAIGNS_PAGE_TITLE}
                </ListItem>
            );
        }

        if (!this.isRowLoaded({ index })) {
            return <ListItemPlaceholder key={key} style={style} icon isLast />;
        }

        const item = items[index];
        const nextItem = index + 1 < items.length ? items[index + 1] : null;

        let listItem;
        if (item.type === "common-place") {
            const catUrl = forumCommonPlaceTopicsUrl;
            listItem = (
                <ListItem
                    hasMore
                    icon={
                        <ImgCropped
                            imgUrl={`${assetUrl}img/forum/common_place.png`}
                            imgWidth={96}
                            imgHeight={96}
                            width={32}
                            height={32}
                            className={styles.ListItemIcon}
                            mode={ImgCropMode.Cover}
                            alt="icon"
                        />
                    }
                    isLast
                    key={key}
                    style={style}
                    to={catUrl}
                >
                    {FORUM_COMMON_PLACE}
                </ListItem>
            );
        } else if (item.type === "category") {
            const category = item.category;
            listItem = (
                <ListHeadingItem isSectionStart key={key} style={style}>
                    {upperFirst(category.name)}
                </ListHeadingItem>
            );
        } else {
            const topic = item.topic;
            const isNew = lessThan14Days(item.topic.created);
            listItem = (
                <ListItem
                    count={topic.numThreads}
                    icon={<ListItemIcon lazy photo={topic.coverPhoto} />}
                    isLast={
                        (!hasNext && items.length === index + 1) ||
                        (nextItem && nextItem.type === "category")
                    }
                    key={key}
                    rightElement={
                        isNew ? (
                            <div className={styles.ListOfForums__badgeNew}>{NEW_FEMININE}</div>
                        ) : null
                    }
                    style={style}
                    to={itemToTopicUrl(topic)}
                >
                    {upperFirst(topic.name)}
                </ListItem>
            );
        }

        return listItem;
    };

    private renderHeaders = () => {
        const { loadingState, hasNext, hasPrevious, baseUrl, items, location } = this.props;
        if (isSuccess(loadingState)) {
            const qs = pick<{ after: string }>(parse(location.search) as { after: string }, [
                "after",
            ]);

            return (
                <Helmet>
                    {hasNext && (
                        <link
                            rel="next"
                            href={
                                baseUrl +
                                url(
                                    forumListOfForumsUrl,
                                    {},
                                    { ...qs, after: items[items.length - 1].topicId },
                                )
                            }
                        />
                    )}
                    {hasPrevious && (
                        <link
                            rel="prev"
                            href={
                                baseUrl +
                                url(forumListOfForumsUrl, {}, { ...qs, after: -items[0].topicId })
                            }
                        />
                    )}
                </Helmet>
            );
        }

        return null;
    };
}

export default ListOfForums;
